import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { startGoogleSignIn } from "@/lib/googleAuth";
import Logo from "@/components/Logo";
import { ArrowRight, GraduationCap, Heart, Briefcase } from "@phosphor-icons/react";

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.61z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.87-3.06.87-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.71a5.4 5.4 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.9 11.42 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
    </svg>
  );
}


const ROLES = [
  { id: "school", label: "School", desc: "Collect fees & onboard students", icon: GraduationCap },
  { id: "parent", label: "Parent", desc: "Pay, save or finance term fees", icon: Heart },
  { id: "agent", label: "Agent (DSA)", desc: "Earn commission for sign-ups", icon: Briefcase },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") || "parent");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [referral, setReferral] = useState(params.get("ref") || "");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const payload = { role, name, email, password, phone };
      if (referral) payload.referral_code = referral;
      const u = await register(payload);
      const map = { school: "/school", parent: "/parent", agent: "/agent" };
      nav(map[u.role] || "/dashboard");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F4] grain">
      <div className="max-w-5xl mx-auto p-8 md:p-12">
        <Logo testid="register-logo" />

        <div className="mt-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-3">Create account</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-[#132A13]">Choose how you'll use Ascentremit.</h1>
            <p className="text-[#4F5D4F] mt-5 leading-relaxed">Pick a role below — you can always invite teammates with different roles later.</p>
            <div className="mt-8 space-y-3">
              {ROLES.map((r) => (
                <button key={r.id} type="button" onClick={() => setRole(r.id)}
                  className={`w-full text-left p-4 rounded-2xl border transition-all flex items-start gap-4 ${role === r.id ? "border-[#0A3622] bg-white shadow-sm" : "border-[#E5E3DB] hover:border-[#0A3622]/30"}`}
                  data-testid={`role-option-${r.id}`}>
                  <div className={`w-10 h-10 rounded-xl grid place-items-center ${role === r.id ? "bg-[#0A3622] text-[#F2B632]" : "bg-[#F0EFEA] text-[#4F5D4F]"}`}>
                    <r.icon size={20} weight="fill" />
                  </div>
                  <div>
                    <div className="font-display font-medium text-[#132A13]">{r.label}</div>
                    <div className="text-sm text-[#4F5D4F]">{r.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 card !p-10" data-testid="register-form">
            <h2 className="font-display text-2xl font-medium text-[#132A13]">Tell us about you</h2>
            <p className="text-sm text-[#4F5D4F] mt-1">We never share data with third parties.</p>

            {role === "parent" && (
              <div className="mt-6">
                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <button
                  type="button"
                  onClick={() => { startGoogleSignIn(); }}
                  className="w-full flex items-center justify-center gap-2 border border-[#E5E3DB] hover:border-[#0A3622] bg-white text-[#132A13] font-medium rounded-full py-3 px-4 transition-colors text-sm"
                  data-testid="register-google-button"
                >
                  <GoogleLogo /> Continue with Google
                </button>
                <div className="flex items-center gap-3 mt-4 text-[11px] uppercase tracking-[0.14em] text-[#4F5D4F]/70">
                  <div className="h-px flex-1 bg-[#E5E3DB]" />
                  <span>or with email</span>
                  <div className="h-px flex-1 bg-[#E5E3DB]" />
                </div>
              </div>
            )}

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input className="input mt-1" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Adebayo" data-testid="register-name-input" />
              </Field>
              <Field label="Phone">
                <input className="input mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+234 80…" data-testid="register-phone-input" />
              </Field>
              <Field label="Email" required>
                <input type="email" className="input mt-1" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="register-email-input" />
              </Field>
              <Field label="Password" required>
                <input type="password" className="input mt-1" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6+ characters" data-testid="register-password-input" />
              </Field>
              {(role === "parent" || role === "school") && (
                <Field label="DSA referral code (optional)">
                  <input className="input mt-1" value={referral} onChange={(e) => setReferral(e.target.value)} placeholder="DSA-XXXXXX" data-testid="register-referral-input" />
                </Field>
              )}
            </div>

            {err && <p className="text-sm text-[#C93B3B] mt-4" data-testid="register-error">{err}</p>}

            <button type="submit" disabled={loading} className="btn-primary mt-8 justify-center w-full sm:w-auto" data-testid="register-submit-button">
              {loading ? "Creating…" : <>Create my {role} account <ArrowRight size={16} weight="bold" /></>}
            </button>

            <p className="text-sm text-[#4F5D4F] mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-[#F2B632] hover:underline font-medium" data-testid="register-login-link">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{label}{required && <span className="text-[#F2B632]"> *</span>}</span>
      {children}
    </label>
  );
}
